import {
  Box,
  FieldAddonDropdown,
  FieldText,
  FieldTextProps,
} from '@dixa/lib-shared-habla';
import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { usePrevious, useUpdateEffect } from 'react-use';

import { handleParseStringAsInt } from '../../utils/parseStringAsInt';

export enum Unit {
  Seconds = 'Seconds',
  Minutes = 'Minutes',
  Hours = 'Hours',
}

type TimeState = { unit: Unit; value: number };

type UnitProps = {
  /* Unit field name */
  name: string;
  /* Available options */
  options?: Unit[];
  /* Default value */
  default?: number;
};

type InputProps = Omit<FieldTextProps, 'onChange' | 'value'> & {
  value: number | string | undefined;
  onChange: (value: number) => void;
  onBlur: (value: number) => void;
};

type Props = {
  unit: UnitProps;
  input: InputProps;
} & Omit<FieldTextProps, 'suffix' | 'input'>;

type ReduxFormHandler = InputProps['onChange'] | InputProps['onBlur'];

function convertSecondsToHighestUnit(
  seconds: number,
  options: Unit[] = [Unit.Seconds, Unit.Minutes, Unit.Hours],
): {
  unit: Unit;
  value: number;
} {
  const hours = seconds / 60 / 60;
  const minutes = seconds / 60;

  const isOnlyHours = hours !== 0 && hours % 1 === 0;
  const isOnlyMinutes = minutes !== 0 && minutes % 1 === 0;

  if (isOnlyHours && options.includes(Unit.Hours))
    return { unit: Unit.Hours, value: hours };

  if (isOnlyMinutes && options.includes(Unit.Hours))
    return { unit: Unit.Minutes, value: minutes };

  return { unit: Unit.Seconds, value: seconds };
}

function convertUnitToSeconds(unit: Unit, value: number): number {
  switch (unit) {
    case Unit.Hours:
      return value * 60 * 60;
    case Unit.Minutes:
      return value * 60;
    default:
      return value;
  }
}

function getHighestUnitValueFromInput(
  input: Props['input'],
  options?: Unit[],
): TimeState {
  const { value } = input;

  return convertSecondsToHighestUnit(
    handleParseStringAsInt(`${value || 0}`) || 0,
    options,
  );
}

export function FieldTimeInput({ unit, input, ...props }: Props): JSX.Element {
  const unitOptions = Object.values(unit.options || Unit).map((unit) => ({
    id: unit,
    name: unit,
  }));
  const [firstUnitOption] = unitOptions;
  const [{ unit: selectedUnit, value: time }, setTime] = useState<TimeState>(
    () =>
      getHighestUnitValueFromInput(
        {
          ...input,
          value: input.value || unit.default,
        },
        unit.options,
      ),
  );

  const setReduxFormSecondsValue = (
    unit: Unit,
    value: number,
    reduxFormHandler: ReduxFormHandler,
  ) => {
    const seconds = convertUnitToSeconds(unit, value);

    reduxFormHandler(seconds);
  };

  const suffix =
    unitOptions.length > 1
      ? {
          type: 'custom',
          noWrap: true,
          value: (
            <FieldAddonDropdown
              meta={{
                selectedValue: selectedUnit,
              }}
              items={unitOptions}
              onSelect={({ id }) => {
                setTime(({ value }) => ({ unit: id, value }));
                setReduxFormSecondsValue(id, time, input.onChange);
              }}
            />
          ),
        }
      : firstUnitOption.name;

  const handleConvertUnitToSeconds =
    (reduxFormHandler: ReduxFormHandler) =>
    (event?: ChangeEvent<HTMLInputElement>) => {
      const { value = '' } = event?.target || {};
      const time = handleParseStringAsInt(value) || 0;

      setTime(({ unit }) => ({ unit, value: time }));
      setReduxFormSecondsValue(selectedUnit, time, reduxFormHandler);
    };

  const prevInputValue = usePrevious(input.value);
  const prevTimeValue = usePrevious(time);

  useUpdateEffect(() => {
    const { value } = input;

    // We want to update the input if the `value` has been changed by something else.
    // This can happen when e.g saving or if the field value is not immediately populated.
    if (prevInputValue !== value && prevTimeValue === time) {
      setTime(getHighestUnitValueFromInput(input, unit.options));
    }
  }, [prevInputValue, prevTimeValue, time, input]);

  return (
    <Box maxWidth="240px">
      <FieldText
        {...props}
        type="number"
        input={{
          // input has some listeners, e.g onTouch, onBlur, etc.
          // these are necessary to be called for meta values to be shown.
          ...input,
          name: unit.name,
          value: `${time}`,
          onBlur: handleConvertUnitToSeconds(input.onBlur),
          onChange: handleConvertUnitToSeconds(input.onChange),
        }}
        suffix={suffix}
      />
    </Box>
  );
}
